import { Globe } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

import Tooltip from "@/components/Tooltip";
import { LANGUAGES } from "@/lib/constants";

/**
 * Single-button language toggle (Pfizer / japantollreceipts.jp style).
 *
 * Shows ONLY the opposite language (with globe icon). Clicking flips it.
 *   - On /ja  → renders "🌐 EN"
 *   - On /en  → renders "🌐 JA"
 *
 * Wrapped in a Tooltip so visitors who hover or focus the pill see a
 * branded "Switch language to Japanese / English" hint drop down below
 * the button before they click.
 */
export default function LanguageSwitcher({ lang }) {
  const navigate = useNavigate();
  const location = useLocation();

  const target = lang === "ja" ? "en" : "ja";
  const meta = LANGUAGES[target] || { code: target, short: target.toUpperCase() };
  const tipLabel = target === "ja"
    ? "Switch language to Japanese"
    : "Switch language to English";

  const swap = () => {
    const parts = location.pathname.split("/").filter(Boolean);
    // Preserve query string + hash so pagination, filters, deep-links
    // (?page=9, ?status=all, ?release=v1.0.0, #section-id) carry across
    // the language flip. Owner directive 2026-05-19: switching from EN ⇄ JA
    // on testimonials page 9 should keep you on page 9, not bounce you back
    // to page 1.
    const tail = `${location.search || ""}${location.hash || ""}`;
    if (parts.length === 0) {
      navigate(`/${target}${tail}`);
      return;
    }
    parts[0] = target;
    navigate(`/${parts.join("/")}${tail}`);
  };

  return (
    <Tooltip
      label={tipLabel}
      accent="green"
      placement="bottom"
      delay={0}
      testId="language-switcher-tooltip"
    >
      <button
        type="button"
        data-testid="language-switcher"
        onClick={swap}
        aria-label={tipLabel}
        className="inline-flex items-center gap-1.5 h-11 md:h-9 px-4 md:px-3.5 rounded-full bg-[#78D33C] border border-[#78D33C] text-white active:bg-[#5FB825] hover:bg-[#5FB825] hover:border-[#5FB825] shadow-[0_4px_14px_-4px_rgba(120,211,60,0.55)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#78D33C] focus-visible:ring-offset-2 touch-manipulation select-none"
      >
        <Globe className="h-3.5 w-3.5" strokeWidth={1.75} />
        <span className="font-mono-display text-[12px] tracking-[0.18em] font-bold uppercase leading-none">
          {meta.short}
        </span>
      </button>
    </Tooltip>
  );
}
